const { createExtensionMethods } = require('./extension');
const GraphQLClientBase = require('./base');

/**
 * An http client that reads/writes OCAP blockchain data. CBOR-only wire
 * encoding — browser / Worker / Node bundles stay off the protobuf runtime.
 *
 * For back-compat with chain nodes that still decode legacy protobuf
 * transactions, or to test the dual-encoding path end-to-end, import the
 * `@ocap/client/legacy` subpath which ships both encoders.
 *
 * Internal protobuf repeated-field naming (`xxxList`) is preserved in
 * response objects the chain returns — this is a server-side convention.
 *
 * @class
 * @example
 * const GraphQLClient = require('@ocap/client');
 *
 * const client = new GraphQLClient('https://beta.abtnetwork.io/api');
 *
 * const res = await client.getChainInfo();
 */
class GraphQLClient extends GraphQLClientBase {
  /**
   * Create an instance of GraphQLClient
   *
   * @constructor
   * @param {string} endpoint the graphql endpoint of the chain
   * @param {boolean} autoInit whether auto warmup context
   * @param {object} [options] reserved for future use
   * @see GraphQLClient#getQueries
   * @see GraphQLClient#getMutations
   * @see GraphQLClient#getSubscriptions
   * @see GraphQLClient#getTxSendMethods
   * @see GraphQLClient#getTxEncodeMethods
   */
  constructor(endpoint, autoInit = true, options = {}) {
    super(endpoint);
    // Phase 4.10 — the main entry is CBOR-only. `options.encoding` is
    // rejected here so callers that still pass it don't silently get a CBOR
    // client when they expected protobuf. Swap to `@ocap/client/legacy`
    // (the only supported way to get the protobuf path now).
    // biome-ignore lint/complexity/useOptionalChain: babel-preset-env 1.7 cannot parse optional chaining
    if (options && options.encoding && options.encoding !== 'cbor') {
      throw new Error(
        `@ocap/client main entry is CBOR-only (got encoding=${JSON.stringify(
          options.encoding
        )}). Import '@ocap/client/legacy' for the protobuf path.`
      );
    }
    this.txEncoding = 'cbor';
    createExtensionMethods(this);

    // warmup the context
    if (autoInit) {
      this.getContext().catch((err) => {
        console.warn('failed to warmup context', err.message);
      });
    }
  }
}

module.exports = GraphQLClient;
